import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import AddIcon from "@material-ui/icons/Add";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import {
  carbonReferenceChoices,
  fetchCarbonOriginChoices,
} from "./corporateCarbon/referenceData";
import {
  CarbonAdminPage,
  CarbonConfirmDialog,
  CarbonDataTable,
  CarbonEditSurface,
  CarbonForm,
  CarbonFormToolbar,
  CarbonSection,
  CarbonSelectInput,
  CarbonStatus,
  CarbonTableActions,
  CarbonTableRow,
  CarbonTextInput,
  CarbonPageAction,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonResourcePath } from "./corporateCarbon/workflow";
const empty = {
  code: "",
  name: "",
  siteTypeId: "",
  controlRelationshipId: "",
  originId: "",
  city: "",
  area: "",
  areaUnitId: "",
  employeeCount: "",
};
const emptySource = {
  label: "",
  category: "electricity",
  fuelId: "",
  vehicleCategoryId: "",
  refrigerantId: "",
};

export const CarbonSiteSources = ({ token, site }) => {
  const generation = useRef(0),
    pendingRef = useRef(false);
  const [sources, setSources] = useState([]),
    [choices, setChoices] = useState({
      fuels: [],
      vehicles: [],
      refrigerants: [],
    }),
    [form, setForm] = useState(emptySource),
    [creating, setCreating] = useState(false),
    [editing, setEditing] = useState(null),
    [retireTarget, setRetireTarget] = useState(null),
    [pending, setPending] = useState(false),
    [loading, setLoading] = useState(true),
    [failed, setFailed] = useState(false),
    [error, setError] = useState("");
  const current = (g) => g === generation.current;
  const load = useCallback(
    async (g) => {
      setLoading(true);
      setFailed(false);
      try {
        const [items, fuels, vehicles, refrigerants] = await Promise.all([
          carbonRequest(
            token,
            `corporate-carbon/sites/${site.id}/sources?includeRetired=true`,
          ),
          carbonReferenceChoices(token, "fuel", true),
          carbonReferenceChoices(token, "vehicleCategory", true),
          carbonReferenceChoices(token, "refrigerant", true),
        ]);
        if (current(g)) {
          if (!Array.isArray(items))
            throw new Error("Invalid emission source response.");
          setSources(items);
          setChoices({ fuels, vehicles, refrigerants });
        }
      } catch (e) {
        if (current(g)) {
          setError(e.message);
          setFailed(true);
        }
      } finally {
        if (current(g)) setLoading(false);
      }
    },
    [token, site.id],
  );
  useEffect(() => {
    const g = ++generation.current;
    setSources([]);
    setCreating(false);
    setEditing(null);
    setRetireTarget(null);
    setError("");
    pendingRef.current = false;
    setPending(false);
    if (token && site.id) load(g);
    return () => {
      if (current(g)) generation.current = g + 1;
    };
  }, [token, site.id, site.status, load]);
  const change = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const selector = (label, key, values) => (
    <CarbonSelectInput
      choices={values.filter((value) => !value.retired)}
      label={label}
      name={key}
      onChange={(event) => change(key, event.target.value)}
      value={form[key]}
    />
  );
  const create = async (event) => {
    event.preventDefault();
    if (pendingRef.current || site.status !== "active") return;
    const needsFuel = ["stationary_combustion", "mobile_combustion"].includes(
      form.category,
    );
    if (
      !form.label.trim() ||
      form.label.trim().length > 255 ||
      (needsFuel && !form.fuelId) ||
      (form.category === "mobile_combustion" && !form.vehicleCategoryId) ||
      (form.category === "refrigerant" && !form.refrigerantId)
    ) {
      setError("Complete all required emission source fields.");
      return;
    }
    const payload = {
      label: form.label.trim(),
      category: form.category,
      ...(needsFuel && { fuelId: form.fuelId }),
      ...(form.category === "mobile_combustion" && {
        vehicleCategoryId: form.vehicleCategoryId,
      }),
      ...(form.category === "refrigerant" && {
        refrigerantId: form.refrigerantId,
      }),
    };
    const g = generation.current;
    pendingRef.current = true;
    setPending(true);
    setError("");
    try {
      await carbonRequest(token, `corporate-carbon/sites/${site.id}/sources`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (current(g)) {
        setCreating(false);
        setForm(emptySource);
        await load(g);
      }
    } catch (e) {
      if (current(g)) setError(e.message);
    } finally {
      if (current(g)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  const update = async (event) => {
    event.preventDefault();
    if (pendingRef.current || site.status !== "active") return;
    if (!form.label.trim() || form.label.trim().length > 255) {
      setError("A valid source label is required.");
      return;
    }
    const g = generation.current;
    pendingRef.current = true;
    setPending(true);
    setError("");
    try {
      await carbonRequest(
        token,
        `corporate-carbon/sites/${site.id}/sources/${editing.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ label: form.label.trim() }),
        },
      );
      if (current(g)) {
        setEditing(null);
        await load(g);
      }
    } catch (e) {
      if (current(g)) setError(e.message);
    } finally {
      if (current(g)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  const retire = async (source) => {
    if (
      pendingRef.current ||
      site.status !== "active" ||
      !source
    )
      return;
    const g = generation.current;
    pendingRef.current = true;
    setPending(true);
    setError("");
    try {
      await carbonRequest(
        token,
        `corporate-carbon/sites/${site.id}/sources/${source.id}/retire`,
        { method: "POST" },
      );
      if (current(g)) {
        await load(g);
        setRetireTarget(null);
      }
    } catch (e) {
      if (current(g)) setError(e.message);
    } finally {
      if (current(g)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  const referenceText = (values, id) => {
    const reference = values.find((value) => value.id === id);
    if (!reference) return id || "Unknown";
    return `${reference.name || reference.code || reference.id}${
      reference.code && reference.code !== reference.name
        ? ` (${reference.code})`
        : ""
    }`;
  };
  const configurationText = (source) => {
    const configuration = source.configuration || {};
    if (source.category === "stationary_combustion")
      return `Fuel: ${referenceText(choices.fuels, configuration.fuelId)}`;
    if (source.category === "mobile_combustion")
      return `Fuel: ${referenceText(
        choices.fuels,
        configuration.fuelId,
      )}; Vehicle category: ${referenceText(
        choices.vehicles,
        configuration.vehicleCategoryId,
      )}`;
    if (source.category === "refrigerant")
      return `Refrigerant: ${referenceText(
        choices.refrigerants,
        configuration.refrigerantId,
      )}`;
    return "Configured per activity";
  };
  return (
    <>
    <CarbonSection
      title={`Emission sources for ${site.code}`}
      description={`Sources assigned to ${site.name || site.code}. Select Edit to change a label or Archive to remove a source from future activity entry.`}
      actions={site.status === "active" ? (
        <Button
          color="primary"
          disabled={pending || loading || failed}
          onClick={() => {
            setForm(emptySource);
            setCreating(true);
            setEditing(null);
            setError("");
          }}
          variant="contained"
        >
          Create emission source
        </Button>
      ) : null}
    >
      {loading && <div role="status">Loading emission sources...</div>}
      {error && <Typography role="alert">{error}</Typography>}
      {failed && (
        <Button onClick={() => load(generation.current)}>
          Retry loading emission sources
        </Button>
      )}
      <CarbonEditSurface
        description="Create a source for future activity entry at this site."
        onClose={() => { if (!pending) setCreating(false); }}
        open={creating}
        title="Create emission source"
      >
        {creating && error ? <Typography color="error" role="alert">{error}</Typography> : null}
        <CarbonForm aria-label="Emission source form" onSubmit={create}>
          <CarbonTextInput
            label="Source label"
            name="sourceLabel"
            value={form.label}
            onChange={(event) => change("label", event.target.value)}
          />
          <CarbonSelectInput
            choices={[
              { id: "electricity", name: "Electricity" },
              { id: "purchased_energy", name: "Heat / steam" },
              { id: "stationary_combustion", name: "Stationary combustion" },
              { id: "mobile_combustion", name: "Fleet" },
              { id: "refrigerant", name: "Refrigerant" },
            ]}
            emptyText={null}
            label="Source category"
            name="sourceCategory"
            value={form.category}
            onChange={(event) => change("category", event.target.value)}
          />
          {["stationary_combustion", "mobile_combustion"].includes(
            form.category,
          ) && selector("Fuel", "fuelId", choices.fuels)}
          {form.category === "mobile_combustion" &&
            selector("Vehicle category", "vehicleCategoryId", choices.vehicles)}
          {form.category === "refrigerant" &&
            selector("Refrigerant", "refrigerantId", choices.refrigerants)}
          <CarbonFormToolbar>
            <Button color="primary" type="submit" disabled={pending} variant="contained">
              {pending ? "Saving..." : "Save emission source"}
            </Button>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
          </CarbonFormToolbar>
        </CarbonForm>
      </CarbonEditSurface>
      <CarbonEditSurface
        description="Changing this label does not alter finalized inventories or historical calculations."
        onClose={() => { if (!pending) setEditing(null); }}
        open={Boolean(editing)}
        title={editing ? `Edit ${editing.label}` : "Edit emission source"}
      >
        {editing && error ? <Typography color="error" role="alert">{error}</Typography> : null}
        <CarbonForm aria-label="Emission source label form" onSubmit={update}>
          <CarbonTextInput
            label="Source label"
            name="sourceLabelEdit"
            value={form.label}
            onChange={(event) => change("label", event.target.value)}
          />
          <CarbonFormToolbar>
            <Button color="primary" type="submit" disabled={pending} variant="contained">
              {pending ? "Saving..." : "Save source label"}
            </Button>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
          </CarbonFormToolbar>
        </CarbonForm>
      </CarbonEditSurface>
      {!loading && !failed && sources.length === 0 && (
        <Typography>No emission sources exist for this site.</Typography>
      )}
      {!loading && !failed && sources.length > 0 && (
        <CarbonDataTable label={`Emission sources for ${site.code}`}>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.map((source) => (
              <TableRow key={source.id}>
                <TableCell><span>{source.label}<br />{configurationText(source)}</span></TableCell>
                <TableCell>{source.category.replace(/_/g, " ")}</TableCell>
                <TableCell><CarbonStatus value={source.status} /></TableCell>
                <TableCell align="right">
                  {site.status === "active" && source.status === "active" ? (
                    <CarbonTableActions label={`Actions for ${source.label}`}>
                      <Button
                        aria-label={`Edit ${source.label}`}
                        disabled={pending}
                        onClick={() => {
                          setEditing(source);
                          setCreating(false);
                          setForm({ ...emptySource, label: source.label });
                          setError("");
                        }}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Button aria-label={`Archive ${source.label}`} color="secondary" disabled={pending} onClick={() => setRetireTarget(source)} size="small">
                        Archive
                      </Button>
                    </CarbonTableActions>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </CarbonDataTable>
      )}
    </CarbonSection>
    <CarbonConfirmDialog
      confirmLabel={pending ? "Archiving…" : "Archive"}
      description="The source remains in historical records but will not be available for future activity entry."
      disabled={pending}
      onCancel={() => setRetireTarget(null)}
      onConfirm={() => retire(retireTarget)}
      open={Boolean(retireTarget)}
      title={retireTarget ? `Archive emission source ${retireTarget.label}?` : "Archive emission source?"}
    />
    </>
  );
};
export const CarbonSitePage = ({ accessToken, createOnly = false, editSiteId = "", history }) => {
  const contextToken = useTokenOps().account?.accessToken,
    token = accessToken || contextToken;
  const generation = useRef(0),
    pendingRef = useRef(false);
  const [sites, setSites] = useState([]),
    [form, setForm] = useState(empty),
    [editing, setEditing] = useState(null),
    [sourceSite, setSourceSite] = useState(null),
    [archiveTarget, setArchiveTarget] = useState(null),
    [open, setOpen] = useState(createOnly),
    [pending, setPending] = useState(false),
    [loading, setLoading] = useState(true),
    [failed, setFailed] = useState(false),
    [error, setError] = useState(""),
    [reload, setReload] = useState(0),
    [choices, setChoices] = useState({
      types: [],
      controls: [],
      origins: [],
      units: [],
    });
  const current = (g) => g === generation.current;
  const load = useCallback(
    async (g) => {
      setLoading(true);
      setFailed(false);
      try {
        const [s, types, controls, origins, units] = await Promise.all([
          carbonRequest(token, "corporate-carbon/sites?includeArchived=true"),
          carbonReferenceChoices(token, "siteType"),
          carbonReferenceChoices(token, "controlRelationship"),
          fetchCarbonOriginChoices(token),
          carbonReferenceChoices(token, "quantityUnit"),
        ]);
        if (current(g)) {
          setSites(Array.isArray(s) ? s : []);
          setChoices({ types, controls, origins, units });
        }
      } catch (e) {
        if (current(g)) {
          setError(e.message);
          setFailed(true);
        }
      } finally {
        if (current(g)) setLoading(false);
      }
    },
    [token],
  );
  useEffect(() => {
    const g = ++generation.current;
    setSites([]);
    setOpen(createOnly);
    setEditing(null);
    setSourceSite(null);
    setError("");
    pendingRef.current = false;
    setPending(false);
    if (token) load(g);
    return () => {
      if (current(g)) generation.current = g + 1;
    };
  }, [token, reload, load, createOnly]);
  const change = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const select = (label, key, items) => (
    <CarbonSelectInput
      choices={items}
      choiceText={(item) => item.name || item.label?.en || item.code}
      label={label}
      name={key}
      value={form[key]}
      onChange={(event) => change(key, event.target.value)}
    />
  );
  const field = (label, key, type = "text") => (
    <CarbonTextInput
      label={label}
      name={key}
      type={type}
      value={form[key]}
      onChange={(event) => change(key, event.target.value)}
    />
  );
  const start = (site = null) => {
    setEditing(site);
    setForm(
      site
        ? Object.fromEntries(Object.keys(empty).map((k) => [k, site[k] ?? ""]))
        : empty,
    );
    setOpen(true);
    setError("");
  };
  useEffect(() => {
    if (!editSiteId || loading) return;
    const site = sites.find((item) => item.id === editSiteId);
    if (site && editing?.id !== site.id) start(site);
  }, [editSiteId, loading, sites, editing]);
  const submit = async (e) => {
    e.preventDefault();
    if (pendingRef.current) return;
    const areaNumber = form.area === "" ? null : Number(form.area);
    const employeeNumber =
      form.employeeCount === "" ? null : Number(form.employeeCount);
    if (
      !form.code.trim() ||
      form.code.trim().length > 64 ||
      !form.name.trim() ||
      form.name.trim().length > 255 ||
      !form.city.trim() ||
      form.city.trim().length > 255 ||
      !form.siteTypeId ||
      !form.controlRelationshipId ||
      !form.originId ||
      (form.area === "") !== (form.areaUnitId === "") ||
      (areaNumber !== null &&
        (!Number.isFinite(areaNumber) ||
          areaNumber < 0 ||
          areaNumber >= 1e18 ||
          !/^\d+(?:\.\d{1,6})?$/.test(form.area))) ||
      (employeeNumber !== null &&
        (!Number.isInteger(employeeNumber) || employeeNumber < 0))
    ) {
      setError("Complete all required fields with valid bounded values.");
      return;
    }
    const g = generation.current;
    pendingRef.current = true;
    setPending(true);
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      siteTypeId: form.siteTypeId,
      controlRelationshipId: form.controlRelationshipId,
      originId: form.originId,
      city: form.city.trim(),
      ...(form.area !== "" && {
        area: Number(form.area),
        areaUnitId: form.areaUnitId,
      }),
      ...(editing &&
        form.area === "" &&
        form.areaUnitId === "" &&
        (editing.area != null || editing.areaUnitId != null) && {
          area: null,
          areaUnitId: null,
        }),
      ...(form.employeeCount !== "" && {
        employeeCount: employeeNumber,
      }),
      ...(editing &&
        form.employeeCount === "" &&
        editing.employeeCount != null && { employeeCount: null }),
    };
    try {
      await carbonRequest(
        token,
        editing
          ? `corporate-carbon/sites/${editing.id}`
          : "corporate-carbon/sites",
        {
          method: editing ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (current(g)) {
        setOpen(false);
        if (history) {
          history.push(editing ? carbonResourcePath("carbonSites", editing.id) : "/carbonSites");
        } else {
          setReload((v) => v + 1);
        }
      }
    } catch (e2) {
      if (current(g)) setError(e2.message);
    } finally {
      if (current(g)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  const archive = async (site) => {
    if (pendingRef.current || !site) return;
    const g = generation.current;
    pendingRef.current = true;
    setPending(true);
    try {
      await carbonRequest(token, `corporate-carbon/sites/${site.id}/archive`, {
        method: "POST",
      });
      if (current(g)) {
        setArchiveTarget(null);
        setReload((v) => v + 1);
      }
    } catch (e) {
      if (current(g)) setError(e.message);
    } finally {
      if (current(g)) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };
  return (
    <CarbonAdminPage
      title="Carbon sites"
      description="Manage reporting locations and their emission sources for the active account."
      actions={!createOnly && !editSiteId && !open ? (
        <Button
          color="primary"
          disabled={pending || loading || failed}
          onClick={() => start()}
          variant="contained"
        >
          Create site
        </Button>
      ) : null}
    >
        {loading && <div role="status">Loading sites…</div>}
        {error && <Typography role="alert">{error}</Typography>}
        {failed && (
          <Button onClick={() => setReload((v) => v + 1)}>
            Retry loading sites
          </Button>
        )}
        <CarbonEditSurface
          dedicated={createOnly || Boolean(editSiteId)}
          description="Site changes affect future drafts and activity entry. Finalized inventories and calculations remain unchanged."
          onClose={() => {
            if (!pending) setOpen(false);
          }}
          open={open}
          title={editing ? `Edit site ${editing.code}` : "Create site"}
        >
          {error ? <Typography role="alert">{error}</Typography> : null}
          <CarbonForm aria-label="Site form" onSubmit={submit}>
            {field("Site code", "code")}
            {field("Site name", "name")}
            {select("Site type", "siteTypeId", choices.types)}
            {select(
              "Control relationship",
              "controlRelationshipId",
              choices.controls,
            )}
            {select("Country", "originId", choices.origins)}
            {field("City", "city")}
            {field("Area", "area", "number")}
            {select("Area unit", "areaUnitId", choices.units)}
            {field("Employee count", "employeeCount", "number")}
            <CarbonFormToolbar>
            <Button color="primary" type="submit" disabled={pending} variant="contained">
              {pending ? "Saving…" : "Save site"}
            </Button>
              <Button disabled={pending} onClick={() => history ? history.goBack() : setOpen(false)}>Cancel</Button>
            </CarbonFormToolbar>
          </CarbonForm>
        </CarbonEditSurface>
        {!createOnly && !editSiteId && !loading && !failed && sites.length === 0 && (
          <Typography>No sites exist for this account.</Typography>
        )}
        {!createOnly && !editSiteId && !loading && !failed && sites.length > 0 && (
          <CarbonSection
            title="Reporting sites"
            description="Select a site row to view and manage its emission sources."
          >
            <CarbonDataTable label="Carbon sites">
              <TableHead>
                <TableRow>
                  <TableCell>Site</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Control relationship</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Area</TableCell>
                  <TableCell>Employees</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sites.map((s) => (
                  <CarbonTableRow
                    ariaLabel={`View emission sources for ${s.code}`}
                    key={s.id}
                    selected={sourceSite?.id === s.id}
                  >
                    <TableCell><strong>{s.code}</strong><br />{s.name}</TableCell>
                    <TableCell>{choices.types.find((item) => item.id === s.siteTypeId)?.name || "—"}</TableCell>
                    <TableCell>{choices.controls.find((item) => item.id === s.controlRelationshipId)?.name || "—"}</TableCell>
                    <TableCell>{choices.origins.find((item) => item.id === s.originId)?.name || "—"}</TableCell>
                    <TableCell>{s.city || "—"}</TableCell>
                    <TableCell>{s.area == null ? "—" : `${Number(s.area).toLocaleString()} ${choices.units.find((item) => item.id === s.areaUnitId)?.name || ""}`.trim()}</TableCell>
                    <TableCell>{s.employeeCount ?? "—"}</TableCell>
                    <TableCell><CarbonStatus value={s.status} /></TableCell>
                    <TableCell align="right">
                      <CarbonTableActions label={`Actions for ${s.code}`}>
                        <Button color="primary" disabled={pending} onClick={() => setSourceSite(s)} size="small">
                          View sources
                        </Button>
                        {s.status === "active" && (
                          <>
                            <Button disabled={pending} onClick={() => start(s)} size="small">Edit</Button>
                            <Button color="secondary" disabled={pending} onClick={() => setArchiveTarget(s)} size="small">Archive</Button>
                          </>
                        )}
                      </CarbonTableActions>
                    </TableCell>
                  </CarbonTableRow>
                ))}
              </TableBody>
            </CarbonDataTable>
          </CarbonSection>
        )}
        {!createOnly && !editSiteId && sourceSite && <CarbonSiteSources token={token} site={sourceSite} />}
        <CarbonConfirmDialog
          confirmLabel={pending ? "Archiving…" : "Archive"}
          description="Historical inventories and finalized calculations remain unchanged. The site will no longer be available for future boundaries or activity entry."
          disabled={pending}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={() => archive(archiveTarget)}
          open={Boolean(archiveTarget)}
          title={archiveTarget ? `Archive site ${archiveTarget.code}?` : "Archive site?"}
        />
    </CarbonAdminPage>
  );
};

const useCarbonSiteCollection = (accessToken) => {
  const contextToken = useTokenOps().account?.accessToken;
  const token = accessToken || contextToken;
  const [state, setState] = useState({
    sites: [],
    choices: { types: [], controls: [], origins: [], units: [] },
    loading: true,
    error: "",
  });
  useEffect(() => {
    let active = true;
    setState((current) => ({ ...current, loading: true, error: "" }));
    Promise.all([
      carbonRequest(token, "corporate-carbon/sites?includeArchived=true"),
      carbonReferenceChoices(token, "siteType"),
      carbonReferenceChoices(token, "controlRelationship"),
      fetchCarbonOriginChoices(token),
      carbonReferenceChoices(token, "quantityUnit"),
    ])
      .then(([sites, types, controls, origins, units]) => {
        if (!active) return;
        setState({
          sites: Array.isArray(sites) ? sites : [],
          choices: { types, controls, origins, units },
          loading: false,
          error: "",
        });
      })
      .catch((error) => {
        if (active) setState((current) => ({ ...current, loading: false, error: error.message }));
      });
    return () => { active = false; };
  }, [token]);
  return { ...state, token };
};

const choiceName = (choices, id) =>
  choices.find((item) => item.id === id)?.name || "—";

export const CarbonSiteList = ({ accessToken, history }) => {
  const { sites, choices, loading, error } = useCarbonSiteCollection(accessToken);
  const show = (id) => history.push(carbonResourcePath("carbonSites", id));
  return (
    <CarbonAdminPage
      title="Carbon sites"
      description="Manage reporting locations and their emission sources for the active account."
      actions={<CarbonPageAction color="primary" startIcon={<AddIcon />} to={carbonResourcePath("carbonSites", null, "create")}>Create site</CarbonPageAction>}
      variant="list"
    >
      {loading ? <div role="status">Loading sites…</div> : null}
      {error ? <Typography role="alert">{error}</Typography> : null}
      {!loading && !error && sites.length === 0 ? <Typography>No sites exist for this account.</Typography> : null}
      {!loading && !error && sites.length > 0 ? (
        <CarbonSection title="Reporting sites" description="Open a site to view its details and emission sources.">
          <CarbonDataTable label="Carbon sites">
            <TableHead><TableRow><TableCell>Site</TableCell><TableCell>Type</TableCell><TableCell>Control relationship</TableCell><TableCell>Country</TableCell><TableCell>City</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
            <TableBody>{sites.map((site) => (
              <CarbonTableRow ariaLabel={`View site ${site.code}`} key={site.id} onSelect={() => show(site.id)}>
                <TableCell><strong>{site.code}</strong><br />{site.name}</TableCell>
                <TableCell>{choiceName(choices.types, site.siteTypeId)}</TableCell>
                <TableCell>{choiceName(choices.controls, site.controlRelationshipId)}</TableCell>
                <TableCell>{choiceName(choices.origins, site.originId)}</TableCell>
                <TableCell>{site.city || "—"}</TableCell>
                <TableCell><CarbonStatus value={site.status} /></TableCell>
              </CarbonTableRow>
            ))}</TableBody>
          </CarbonDataTable>
        </CarbonSection>
      ) : null}
    </CarbonAdminPage>
  );
};

export const CarbonSiteShow = ({ accessToken, id }) => {
  const { sites, choices, token, loading, error } = useCarbonSiteCollection(accessToken);
  const site = sites.find((item) => item.id === id);
  return (
    <CarbonAdminPage
      title={site ? `${site.code} · ${site.name}` : "Carbon site"}
      description="Site details and emission sources."
      actions={site ? <CarbonPageAction to={carbonResourcePath("carbonSites", site.id, "edit")}>Edit</CarbonPageAction> : null}
    >
      {loading ? <div role="status">Loading site…</div> : null}
      {error ? <Typography role="alert">{error}</Typography> : null}
      {!loading && !error && !site ? <Typography role="alert">Site not found.</Typography> : null}
      {site ? (
        <>
          <CarbonSection title="Site information">
            <dl>
              <dt>Code</dt><dd>{site.code}</dd>
              <dt>Name</dt><dd>{site.name}</dd>
              <dt>Type</dt><dd>{choiceName(choices.types, site.siteTypeId)}</dd>
              <dt>Control relationship</dt><dd>{choiceName(choices.controls, site.controlRelationshipId)}</dd>
              <dt>Country</dt><dd>{choiceName(choices.origins, site.originId)}</dd>
              <dt>City</dt><dd>{site.city || "—"}</dd>
              <dt>Area</dt><dd>{site.area == null ? "—" : `${Number(site.area).toLocaleString()} ${choiceName(choices.units, site.areaUnitId)}`}</dd>
              <dt>Employees</dt><dd>{site.employeeCount ?? "—"}</dd>
              <dt>Status</dt><dd><CarbonStatus value={site.status} /></dd>
            </dl>
          </CarbonSection>
          <CarbonSiteSources token={token} site={site} />
        </>
      ) : null}
    </CarbonAdminPage>
  );
};

export const CarbonSiteCreate = (props) => <CarbonSitePage {...props} createOnly />;
export const CarbonSiteEdit = (props) => <CarbonSitePage {...props} editSiteId={props.id} />;

const carbonSiteResource = () => (
  <Resource
    name="carbonSites"
    options={{ label: "Sites", menuGroup: "Carbon Accounting" }}
    list={CarbonSiteList}
    show={CarbonSiteShow}
    create={CarbonSiteCreate}
    edit={CarbonSiteEdit}
  />
);
export default carbonSiteResource;
